import data from "../../shared/sharedData";

export default {
  name: "Employer Point of Contact Information",
  pageInfo: {
    text: "An employer point of contact is an employee of the employer whose position authorizes the employee to provide information and supporting documentation concerning this <i>H-2A Application for Temporary Employment Certification</i> and to communicate with the Department of Labor on behalf of the employer. The employer point of contact should be the individual most familiar with the content of this application and circumstances of the foreign worker’s employment. <br /><br /><b><u>Note</u></b>: The employer point of contact information in this Section, specifically the name, telephone number, and email address, <u>must</u> be different from the attorney/agent information listed in Section D, unless the person in Section D is an employee of the employer.",
    coloring: "grey",
  },
  sections: [
    {
      fields: [
        {
          widget: "inlinePrefiller",
          prefillLabel:
            "Select an Employer Point of Contact profile to populate this section (optional)",
          code: "C",
          prefill: {
            schema: "employerPocs",
            type: "employer-pocs",
            formName: "9142A",
            formSection: "C",
          },
        },
        {
          code: "C.1",
          machine_name: "c1_poc_last",
          widget: "text",
          label: "Contact's Last (family) Name",
          validate: [
            "required",
            {
              minLength: 2,
              message:
                "Contact's full Last Name must contain at least 2 characters.",
            },
          ],
          maxLength: 35,
          tooltip:
            "Enter the last (family) name of the employer’s point of contact.",
        },
        {
          code: "C.2",
          machine_name: "c2_poc_first",
          widget: "text",
          label: "First (given) Name",
          validate: [
            "required",
            {
              minLength: 2,
              message:
                "Contact's full First Name must contain at least 2 characters.",
            },
          ],
          maxLength: 35,
          tooltip:
            "Enter the first (given) name of the employer’s point of contact.",
        },
        {
          code: "C.3",
          machine_name: "c3_poc_middle",
          widget: "text",
          label: "Middle name(s)",
          maxLength: 35,
          tooltip: "Enter the middle name of the employer’s point of contact.",
        },
        {
          code: "C.4",
          machine_name: "c4_poc_title",
          widget: "text",
          label: "Contact's Job Title",
          validate: [
            "required",
            {
              minLength: 5,
              message:
                "Contact's full Job Title must contain at least 5 characters.",
            },
          ],
          maxLength: 60,
          tooltip: "Enter the job title of the employer's point of contact.",
        },
      ],
    },
    {
      fields: [
        {
          code: "C.5",
          machine_name: "c5_poc_address_1",
          widget: "text",
          label: "Address 1",
          validate: [
            "required",
            {
              minLength: 5,
              message: "Address 1 must contain at least 5 characters.",
            },
          ],
          maxLength: 60,
          tooltip:
            "Enter the business street address of the employer’s point of contact.",
        },
        {
          code: "C.6",
          machine_name: "c6_poc_address_2",
          widget: "text",
          label:
            "<span>Address 2 <i>(apartment/suite/floor and number)</i></span>",
          maxLength: 60,
          tooltip:
            "If additional space is needed for the street address, use this line to complete the street address.",
        },
        {
          code: "C.10",
          machine_name: "c10_poc_country_id",
          widget: "select",
          label: "Country",
          selection: data.country,
          validate: ["required"],
          clearSchema: ["c8_poc_state_id"],
          tooltip:
            "Enter the country of the employer's point of contact. If the city and country are the same, the name <u>must</u> still be entered in <u>both</u> fields.",
        },
        {
          code: "C.7",
          machine_name: "c7_poc_city",
          widget: "text",
          label: "City",
          validate: [
            "required",
            {
              minLength: 2,
              message: "City must contain at least 2 characters.",
            },
          ],
          maxLength: 35,
          tooltip:
            "Enter the city of the employer's point of contact. If the city and country are the same, the name <u>must</u> still be entered in <u>both</u> fields.",
        },
        {
          code: "C.8",
          machine_name: "c8_poc_state_id",
          widget: "select",
          label: "State",
          selection: data.state,
          state: true,
          isVisible: {
            when: "c10_poc_country_id",
            is: "UNITED STATES OF AMERICA",
          },
          validate: ["required"],
          tooltip:
            "Enter the State, District, or Territory of the employer's point of contact.",
        },
        {
          code: "C.9",
          machine_name: "c9_poc_zip",
          widget: "text",
          label: "Postal Code",
          validate: [
            "required",
            {
              postalCountry: "c10_poc_country_id",
              needsFieldValues: true,
              extraData: {
                valueOf: "c10_poc_country_id",
              },
            },
          ],

          maxLength: 10,
          tooltip:
            "Enter the postal (zip) code of the employer's point of contact.",
        },
        {
          code: "C.11",
          machine_name: "c11_poc_province",
          widget: "text",
          label: "Province",
          maxLength: 25,
          validate: [
            {
              minLength: 5,
              message: "Province must contain at least 5 characters.",
            },
          ],
          tooltip:
            "Enter the province of the employer's point of contact, if applicable.",
        },
      ],
    },
    {
      fields: [
        {
          code: "C.12",
          machine_name: "c12_poc_phone",
          widget: "phone",
          label: "Telephone Number",
          validate: ["required", "phone"],
          tooltip:
            "Enter the area code and business telephone number of the employer's point of contact. Include country code, if applicable.",
        },
        {
          code: "C.13",
          machine_name: "c13_poc_phone_ext",
          widget: "text",
          label: "Extension",
          validate: ["number"],
          parse: "positiveWholeNumber", //don't allow decimal Number and negative number // not working for me? -NG
          maxLength: 4,
          tooltip:
            "Enter the extension of the telephone number of the employer's point of contact, if applicable.",
        },
        {
          code: "C.14",
          machine_name: "c14_poc_email",
          widget: "text",
          label: "Business e-mail address",
          validate: [
            "required",
            {
              email: {
                extraData: {
                  allowNAs: true,
                },
              },
            },
          ],
          maxLength: 55,
          tooltip:
            "Enter the business email address of the employer's point of contact in the format <a href='' target='_blank'>name@emailaddress.top-leveldomain</a>. The email entered in this field must be the same as the one regularly used by the employer’s point of contact for its business operations and capable of sending and receiving electronic communications to and from the Department with respect to the processing of this application. If the employer’s point of contact does not possess a business email address, please enter “N/A.”",
        },
      ],
    },
  ],
};
